import React from 'react'
import { Box, Tooltip, Typography } from '@mui/material'

import { OptionSelectInput } from '../InputFields/OptionSelectInput'

import langs from '@/assets/allLangs.json'

interface FeedbackLanguageSelectorProps {
  value?: string
  onChange?: (value: string) => void
  label?: string
  placeholder?: string
  targetLanguage?: keyof typeof langs
  inputStyles: object
  containerClassName?: string
  size?: 'medium' | 'small'
  disabled?: boolean
}

export default function FeedbackLanguageSelector({
  value = '',
  onChange = () => null,
  label = 'Feedback Language',
  targetLanguage = 'en',
  inputStyles = {},
  placeholder = 'Select Language',
  containerClassName = '',
  size = 'medium',
  disabled = false,
}: FeedbackLanguageSelectorProps): JSX.Element {
  const options = [
    { label: langs[targetLanguage], value: 'target' },
    { label: 'Native Language', value: 'native' },
  ]

  return (
    <Box
      className={containerClassName}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {label ? (
        <TitleText
          fontSize={size === 'small' ? 14 : 15}
          link="https://www.notion.so/speakable/A-Guide-to-Speakable-s-Activity-Builder-acc92d9f56624fcfb96ba486fa10b427?pvs=4#007635e5cc1b478e8ce59463cade97a0"
          tooltipText="Select the language for the AI feedback."
        >
          {label}
        </TitleText>
      ) : null}

      <OptionSelectInput
        customStyles={{
          borderColor: 'gray',
          '&.MuiTextField-root': { width: '100%' },
          fontSize: size === 'small' ? 12 : 15,
        }}
        disabled={disabled}
        inputStyles={{
          borderColor: 'gray',
          '& .MuiInputBase-root': {
            height: 44,
            background: '#FFF',
          },
          ...inputStyles,
          fontSize: size === 'small' ? 12 : 15,
        }}
        options={options}
        placeholder={placeholder}
        setValue={onChange}
        value={value}
      />
    </Box>
  )
}

interface TitleTextProps {
  children: React.ReactNode
  tooltipText: string
  subtitle?: string | null
  link?: string
  fontSize?: number
}

export function TitleText({
  children,
  tooltipText,
  subtitle = null,
  link,
  fontSize = 15,
}: TitleTextProps): JSX.Element {
  const handleLinkClick = (e: React.MouseEvent<HTMLDivElement | HTMLSpanElement>) => {
    if (!link) return
    e.stopPropagation()
    window.open(link, '_blank')
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '0px',
        flexDirection: 'column',
      }}
    >
      <Tooltip arrow placement="top" title={tooltipText}>
        <Typography
          fontSize={fontSize}
          fontWeight={500}
          mb={1}
          sx={{
            '&:hover': {
              cursor: link ? 'pointer' : 'default',
              textDecoration: link ? 'underline' : 'none',
            },
          }}
          onClick={handleLinkClick}
        >
          {children}
        </Typography>
      </Tooltip>
      {subtitle ? (
        <Typography color="text.secondary" fontSize={12} fontWeight={400}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  )
}
